/**
 * Row shapes mirroring supabase/migrations/0001_init.sql.
 * Shared by the React app, the Pages Functions, and the cron Worker.
 */

export type DelayMode = 'random_1h' | 'fixed_hours'
/**
 * 'draft'     — being built in the wizard; never sends, never counts as active
 * 'scheduled' — finished, waiting for starts_at; the Worker promotes to active
 */
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'scheduled'

/** Statuses the sender will consider. Drafts and paused campaigns are excluded. */
export const SENDABLE_CAMPAIGN_STATUSES = ['active', 'scheduled'] as const

/**
 * 'scheduled' — waiting for its moment; the only status the sender claims
 * 'paused'    — held by hand. Stays in the Queue, never claimed until resumed
 * 'canceled'  — will never send: canceled by hand, unsubscribed, or suppressed
 */
export type QueueStatus = 'scheduled' | 'paused' | 'sent' | 'failed' | 'canceled'

/** Statuses the cron Worker is allowed to claim. */
export const SENDABLE_STATUSES = ['scheduled'] as const

/**
 * The Queue is a waiting room, not a history: these are the statuses still
 * waiting to go out. sent / failed / canceled are terminal — they leave the
 * Queue and live in Send logs.
 */
export const ACTIVE_QUEUE_STATUSES = ['scheduled', 'paused'] as const

export type ActiveQueueStatus = (typeof ACTIVE_QUEUE_STATUSES)[number]

export function isTerminalStatus(s: QueueStatus): boolean {
  return !(ACTIVE_QUEUE_STATUSES as readonly string[]).includes(s)
}

export interface SmtpAccount {
  id: string
  owner_id: string
  label: string
  host: string
  port: number
  username: string
  password_encrypted: string
  /** default From display name for this mailbox */
  from_name: string
  /** the authenticated mailbox — always the From ADDRESS, never overridden */
  from_email: string
  /** default Reply-To for campaigns using this mailbox */
  reply_to: string | null
  /** default {{sender_title}} for campaigns sending from this mailbox */
  sender_title: string | null
  is_default: boolean
  created_at: string
}

/** What the browser is allowed to see — no ciphertext, ever. */
export type SmtpAccountSafe = Omit<SmtpAccount, 'password_encrypted'> & {
  has_password: boolean
}

export interface Campaign {
  id: string
  /** The auth.users row that owns this. RLS scopes every browser read to it. */
  owner_id: string
  name: string
  smtp_account_id: string | null
  /**
   * The campaign's current template. This is the link the send path resolves
   * through — a queue row's own template_id is only the stamp it was created
   * with, so binding a template later rescues rows already waiting.
   */
  template_id: string | null
  /**
   * false = every lead is queued as immediately due, so the next tick sends it.
   * true = delay_mode applies, with a MIN_DELAY_MINUTES floor.
   */
  delay_enabled: boolean
  delay_mode: DelayMode
  /** LEGACY — kept in sync with delay_minutes. Read delay_minutes. */
  delay_hours: number
  /** Fixed delay in minutes (canonical). 0 = next processing tick. */
  delay_minutes: number | null
  /** For delay_mode 'random_1h': each lead waits a random 0..N minutes. */
  random_window_minutes: number
  daily_cap: number
  throttle_seconds: number
  status: CampaignStatus
  /** overrides the identity's display name */
  from_name: string | null
  /** where replies land; overrides the identity's default */
  reply_to: string | null
  /**
   * The hiring organisation this campaign sends on behalf of — {{company}}.
   * A send-wide constant, never read from lead/CSV data.
   */
  company: string | null
  /** optional {{sender_title}}, overriding the identity default */
  sender_title: string | null
  /** First send is held until this moment. Null = start immediately. */
  starts_at: string | null
  /** Only send between the window bounds, local to send_tz. */
  window_enabled: boolean
  /** Minutes from local midnight (540 = 09:00). */
  window_start_min: number
  window_end_min: number
  /** IANA timezone for starts_at and the window. Null = UTC. */
  send_tz: string | null
  /**
   * Set when the user archives the campaign (0021). Non-null = hidden from the
   * list and the pickers and never sent, with every lead, queue row and log line
   * KEPT so the Dashboard and Analytics still count it. Restore = back to null.
   *
   * Optional because 0021 is applied by hand: until it runs, `select *` simply
   * does not return this key. Read it through isArchived() in shared/archive.ts.
   */
  archived_at?: string | null
  created_at: string
}

/** How body_html was authored — controls which editor reopens. */
export type TemplateBodyMode = 'rich' | 'html'

export interface Template {
  id: string
  owner_id: string
  /**
   * OPTIONAL origin campaign. Null = a standalone library template.
   * It does NOT decide what a campaign sends — campaigns.template_id does.
   */
  campaign_id: string | null
  /** Optional human label for the list; falls back to subject when null. */
  name: string | null
  subject: string
  /** For body_mode 'html' this is the author's markup, stored verbatim. */
  body_html: string
  body_text: string
  body_mode: TemplateBodyMode
  created_at: string
}

export interface Lead {
  id: string
  owner_id: string
  campaign_id: string
  name: string | null
  email: string
  fields: Record<string, string>
  source: string | null
  created_at: string
}

export interface QueueItem {
  id: string
  owner_id: string
  lead_id: string
  campaign_id: string
  template_id: string | null
  scheduled_at: string | null
  status: QueueStatus
  sent_at: string | null
  sent_from: string | null
  error: string | null
  attempts: number
  created_at: string
  /** Set while a sender tick owns this row — "sending now" in the queue UI.
   *  Optional: the columns arrive with 0013; select('*') omits them before it. */
  claim_id?: string | null
  claimed_at?: string | null
}

/** A queue row joined with the lead / campaign / template it points at. */
export interface QueueItemExpanded extends QueueItem {
  leads: Pick<Lead, 'id' | 'name' | 'email' | 'fields'> | null
  campaigns: Pick<Campaign, 'id' | 'name'> | null
  templates: Pick<Template, 'id' | 'subject' | 'body_text' | 'body_html'> | null
}

export interface SendLogRow {
  id: string
  owner_id: string
  queue_id: string | null
  email: string
  status: string
  smtp_response: string | null
  at: string
}

export interface Unsubscribe {
  id: string
  owner_id: string
  email: string
  campaign_id: string | null
  reason: string | null
  at: string
}

/** Open/click tracking event — written only by the /t endpoints. */
export interface EmailEvent {
  id: string
  owner_id: string
  queue_id: string | null
  lead_id: string | null
  campaign_id: string | null
  type: 'open' | 'click'
  url: string | null
  at: string
}

export interface CampaignStats {
  campaign_id: string
  name: string
  status: CampaignStatus
  daily_cap: number
  smtp_account_id: string | null
  total: number
  sent: number
  failed: number
  /** rows still due to go out (status 'scheduled') */
  waiting: number
  paused: number
  canceled: number
  /**
   * Non-terminal rows — anything that can still be delivered. THE number that
   * decides live-vs-done; see campaignLifecycle() in shared/lifecycle.ts.
   */
  remaining: number
  /** rows that reached a terminal state (sent / failed / canceled / rejected) */
  processed: number
  sent_today: number
  last_sent_at: string | null
  /** Passed through from campaigns (0021). See Campaign.archived_at. */
  archived_at?: string | null
}

/** Maps a queue status onto a design-system Badge status token. */
export const STATUS_BADGE: Record<QueueStatus, string> = {
  scheduled: 'scheduled',
  paused: 'pending',
  sent: 'sent',
  failed: 'failed',
  canceled: 'rejected',
}

export const STATUS_LABEL: Record<QueueStatus, string> = {
  scheduled: 'Scheduled',
  paused: 'Paused',
  sent: 'Sent',
  failed: 'Failed',
  canceled: 'Canceled',
}
